/**
 * Bakes a track into a single offscreen canvas.
 *
 * The artwork is generated from exactly the same data the physics uses — the
 * centreline, the runoff width, the patches and the barrier rule — so what the
 * player sees is always what the car will hit.
 */

import { makeRng } from '@shared/math.js';
import { surfaceAt, type BuiltTrack } from '@shared/track.js';
import { SURFACES, type SurfaceId } from '@shared/surfaces.js';
import type { TrackTheme } from '@shared/tracks.js';

export interface BakedTrack {
  canvas: HTMLCanvasElement;
  width: number;
  height: number;
}

/** Colour used for a surface patch drawn on top of the road. */
function patchColor(surface: SurfaceId, theme: TrackTheme): string {
  switch (surface) {
    case 'gravel':
      return '#8d8477';
    case 'dirt':
      return '#8a6f4a';
    case 'sand':
      return '#c4a970';
    case 'mud':
      return '#54402c';
    case 'ice':
      return '#d9ecf7';
    case 'oil':
      return '#15161a';
    case 'boost':
      return '#1fb9d8';
    case 'wet':
      return 'rgba(120,170,210,0.45)';
    case 'kerb':
      return theme.kerbA;
    case 'grass':
      return theme.base;
    default:
      return theme.roadAlt;
  }
}

function drawDither(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  colour: string,
  seed: number,
): void {
  const rng = makeRng(seed);
  ctx.fillStyle = colour;
  for (let py = y; py < y + h; py += 6) {
    for (let px = x; px < x + w; px += 6) {
      if (rng() < 0.34) ctx.fillRect(px + (rng() < 0.5 ? 0 : 3), py + (rng() < 0.5 ? 0 : 3), 3, 3);
    }
  }
}

/**
 * Deterministic scenery. Items are only placed where the physics grid says the
 * cell is plain off-road terrain, so nothing ever spawns on the racing line.
 */
function drawScenery(ctx: CanvasRenderingContext2D, track: BuiltTrack): void {
  const theme = track.def.theme;
  const rng = makeRng(hashString(track.def.id));
  const spacing = theme.decor === 'forest' ? 46 : 62;
  for (let y = 24; y < track.def.height; y += spacing) {
    for (let x = 24; x < track.def.width; x += spacing) {
      const px = x + rng() * spacing * 0.7;
      const py = y + rng() * spacing * 0.7;
      const surface = surfaceAt(track, px, py);
      if (SURFACES[surface].solid) continue;
      if (surface !== track.def.base) continue;
      if (rng() < 0.55) continue;
      drawDecor(ctx, theme, px, py, rng);
    }
  }
}

function drawDecor(
  ctx: CanvasRenderingContext2D,
  theme: TrackTheme,
  x: number,
  y: number,
  rng: () => number,
): void {
  const tree = theme.treeColors[Math.floor(rng() * theme.treeColors.length)]!;
  const building = theme.buildingColors[Math.floor(rng() * theme.buildingColors.length)]!;
  const rx = Math.round(x);
  const ry = Math.round(y);

  switch (theme.decor) {
    case 'forest': {
      // Pine: a dark trunk with two stacked canopies.
      ctx.fillStyle = '#3a2c1c';
      ctx.fillRect(rx + 3, ry + 8, 3, 8);
      ctx.fillStyle = tree;
      ctx.fillRect(rx, ry + 2, 9, 7);
      ctx.fillRect(rx + 2, ry - 4, 5, 7);
      ctx.fillStyle = shade(tree, 0.8);
      ctx.fillRect(rx, ry + 8, 9, 2);
      break;
    }
    case 'desert': {
      if (rng() < 0.45) {
        // Cactus.
        ctx.fillStyle = '#4f7a3f';
        ctx.fillRect(rx + 3, ry - 2, 4, 14);
        ctx.fillRect(rx, ry + 2, 3, 4);
        ctx.fillRect(rx + 7, ry + 5, 3, 4);
      } else {
        ctx.fillStyle = '#a08a63';
        ctx.fillRect(rx, ry, 10, 6);
        ctx.fillStyle = '#8b7550';
        ctx.fillRect(rx, ry + 5, 10, 2);
      }
      break;
    }
    case 'harbour': {
      // Shipping container with corrugation stripes.
      const w = 22 + Math.floor(rng() * 10);
      const h = 12;
      ctx.fillStyle = building;
      ctx.fillRect(rx, ry, w, h);
      ctx.fillStyle = shade(building, 0.72);
      for (let sx = rx + 3; sx < rx + w - 2; sx += 4) ctx.fillRect(sx, ry + 1, 1, h - 2);
      ctx.fillStyle = shade(building, 0.55);
      ctx.fillRect(rx, ry + h - 2, w, 2);
      break;
    }
    case 'alpine': {
      ctx.fillStyle = tree;
      ctx.fillRect(rx, ry + 4, 8, 6);
      ctx.fillStyle = shade(tree, 1.25);
      ctx.fillRect(rx + 1, ry - 2, 6, 6);
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(rx + 1, ry - 2, 6, 2);
      break;
    }
    case 'neon': {
      const w = 30 + Math.floor(rng() * 26);
      const h = 24 + Math.floor(rng() * 20);
      ctx.fillStyle = '#171a22';
      ctx.fillRect(rx, ry, w, h);
      ctx.fillStyle = building;
      ctx.fillRect(rx + 2, ry + 2, w - 4, h - 4);
      // Lit windows.
      const neon = theme.accent;
      ctx.fillStyle = neon;
      for (let wy = ry + 5; wy < ry + h - 5; wy += 6) {
        for (let wx = rx + 4; wx < rx + w - 5; wx += 7) {
          if (rng() < 0.45) ctx.fillRect(wx, wy, 3, 3);
        }
      }
      break;
    }
    default: {
      // Suburban street furniture: houses, fences, the odd tree.
      if (rng() < 0.34) {
        ctx.fillStyle = '#3a2c1c';
        ctx.fillRect(rx + 4, ry + 6, 3, 8);
        ctx.fillStyle = tree;
        ctx.fillRect(rx, ry - 2, 11, 10);
        ctx.fillStyle = shade(tree, 0.82);
        ctx.fillRect(rx, ry + 5, 11, 3);
      } else {
        const w = 26 + Math.floor(rng() * 22);
        const h = 20 + Math.floor(rng() * 14);
        ctx.fillStyle = shade(building, 0.7);
        ctx.fillRect(rx, ry, w, h);
        ctx.fillStyle = building;
        ctx.fillRect(rx, ry, w - 2, h - 3);
        ctx.fillStyle = '#5a4a3c';
        ctx.fillRect(rx, ry + h - 8, w - 2, 5);
        ctx.fillStyle = theme.kerbB;
        ctx.fillRect(rx + 3, ry + 4, 4, 4);
        ctx.fillRect(rx + w - 9, ry + 4, 4, 4);
      }
      break;
    }
  }
}

function shade(colour: string, factor: number): string {
  const hex = colour.replace('#', '');
  const full = hex.length === 3 ? hex.split('').map((c) => c + c).join('') : hex;
  const r = Math.min(255, Math.round(parseInt(full.slice(0, 2), 16) * factor));
  const g = Math.min(255, Math.round(parseInt(full.slice(2, 4), 16) * factor));
  const b = Math.min(255, Math.round(parseInt(full.slice(4, 6), 16) * factor));
  return `rgb(${r},${g},${b})`;
}

function hashString(value: string): number {
  let h = 2166136261;
  for (let i = 0; i < value.length; i++) {
    h ^= value.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

function strokePath(
  ctx: CanvasRenderingContext2D,
  track: BuiltTrack,
  width: number,
  colour: string,
  dashed = false,
  dash: [number, number] = [12, 10],
): void {
  const path = track.path;
  if (path.length < 2) return;
  ctx.strokeStyle = colour;
  ctx.lineWidth = width;
  ctx.lineJoin = 'round';
  ctx.lineCap = 'round';
  if (dashed) ctx.setLineDash(dash);
  ctx.beginPath();
  ctx.moveTo(path[0]!.x, path[0]!.y);
  for (let i = 1; i < path.length; i++) ctx.lineTo(path[i]!.x, path[i]!.y);
  ctx.closePath();
  ctx.stroke();
  if (dashed) ctx.setLineDash([]);
}

export function bakeTrack(track: BuiltTrack): BakedTrack {
  const def = track.def;
  const theme = def.theme;
  const width = def.width;
  const height = def.height;
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('2D canvas context unavailable');

  const barrierZone = def.barriers === 'edges';

  // ------------------------------------------------------------ ground base
  if (barrierZone) {
    // Everything outside the road corridor is solid, so paint it as barrier.
    ctx.fillStyle = '#14161c';
    ctx.fillRect(0, 0, width, height);
    drawDither(ctx, 0, 0, width, height, '#191c23', hashString(def.id) ^ 0x9e37);
  } else {
    ctx.fillStyle = theme.base;
    ctx.fillRect(0, 0, width, height);
    drawDither(ctx, 0, 0, width, height, theme.baseAlt, hashString(def.id));
    drawScenery(ctx, track);
  }

  // --------------------------------------------------------------- barriers
  if (barrierZone) {
    const outer = def.roadWidth + def.runoff * 2;
    // A wall band, then the run-off, then the road: matching the grid exactly.
    strokePath(ctx, track, outer + 26, '#0c0e12');
    strokePath(ctx, track, outer + 20, '#3f4450');
    strokePath(ctx, track, outer + 12, '#5a616e');
  }

  // ------------------------------------------------------------- run-off
  strokePath(ctx, track, def.roadWidth + def.runoff * 2, theme.baseAlt);

  // ---------------------------------------------------------------- kerbs
  if (def.kerbs) {
    strokePath(ctx, track, def.roadWidth + 10, theme.kerbA);
    strokePath(ctx, track, def.roadWidth + 10, theme.kerbB, true, [16, 16]);
  }

  // ----------------------------------------------------------------- road
  strokePath(ctx, track, def.roadWidth, theme.road);
  strokePath(ctx, track, def.roadWidth - 6, theme.roadAlt);

  // Road texture: faint speckle so the tarmac is not a flat slab.
  const roadRng = makeRng(hashString(def.id) ^ 0x51ed);
  ctx.save();
  ctx.globalAlpha = 0.14;
  ctx.fillStyle = '#ffffff';
  for (let i = 0; i < track.path.length; i++) {
    const p = track.path[i]!;
    for (let s = 0; s < 3; s++) {
      if (roadRng() < 0.7) continue;
      const ox = (roadRng() - 0.5) * p.w * 0.8;
      const oy = (roadRng() - 0.5) * p.w * 0.8;
      ctx.fillRect(Math.round(p.x + ox), Math.round(p.y + oy), 2, 2);
    }
  }
  ctx.restore();

  // ----------------------------------------------------------- centre line
  strokePath(ctx, track, 2.5, theme.line, true, [18, 16]);

  // --------------------------------------------------------------- patches
  for (const patch of def.patches ?? []) {
    const colour = patchColor(patch.surface, theme);
    ctx.save();
    ctx.globalAlpha = patch.solid ? 1 : 0.92;
    ctx.fillStyle = colour;
    ctx.strokeStyle = colour;
    if (patch.shape === 'ellipse') {
      ctx.save();
      ctx.translate(patch.x, patch.y);
      ctx.rotate(patch.angle ?? 0);
      ctx.beginPath();
      ctx.ellipse(0, 0, patch.w / 2, patch.h / 2, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    } else if (patch.shape === 'rect') {
      ctx.save();
      ctx.translate(patch.x, patch.y);
      ctx.rotate(patch.angle ?? 0);
      ctx.fillRect(-patch.w / 2, -patch.h / 2, patch.w, patch.h);
      ctx.restore();
    } else {
      ctx.fillRect(patch.x, patch.y, patch.w, patch.h);
    }
    ctx.restore();

    if (patch.solid) {
      // Solid patches are obstacles: give them a rim so they read as walls.
      ctx.strokeStyle = '#20242c';
      ctx.lineWidth = 3;
      if (patch.shape === 'rect') {
        ctx.save();
        ctx.translate(patch.x, patch.y);
        ctx.rotate(patch.angle ?? 0);
        ctx.strokeRect(-patch.w / 2, -patch.h / 2, patch.w, patch.h);
        ctx.restore();
      }
    }
  }

  // -------------------------------------------------------- boost chevrons
  for (const patch of def.patches ?? []) {
    if (patch.surface !== 'boost') continue;
    ctx.save();
    ctx.translate(patch.x, patch.y);
    ctx.rotate(patch.angle ?? 0);
    ctx.fillStyle = '#bdf3ff';
    const chevrons = Math.max(2, Math.floor(patch.w / 44));
    for (let i = 0; i < chevrons; i++) {
      const cx = -patch.w / 2 + 12 + i * (patch.w / chevrons);
      ctx.beginPath();
      ctx.moveTo(cx, -patch.h * 0.32);
      ctx.lineTo(cx + 14, 0);
      ctx.lineTo(cx, patch.h * 0.32);
      ctx.lineTo(cx + 6, 0);
      ctx.closePath();
      ctx.fill();
    }
    ctx.restore();
  }

  drawStartLine(ctx, track);
  return { canvas, width, height };
}

/** Chequered start/finish stripe drawn across the road at the first node. */
function drawStartLine(ctx: CanvasRenderingContext2D, track: BuiltTrack): void {
  const p = track.path[0]!;
  const squares = Math.max(6, Math.round(p.w / 16));
  const cell = p.w / squares;
  const rows = 2;
  const depth = cell;
  ctx.save();
  ctx.translate(p.x, p.y);
  // +90° so the stripe's local X runs across the road.
  ctx.rotate(p.angle + Math.PI / 2);
  for (let row = 0; row < rows; row++) {
    for (let i = 0; i < squares; i++) {
      ctx.fillStyle = (i + row) % 2 === 0 ? '#f2f2ef' : '#1b1d22';
      ctx.fillRect(-p.w / 2 + i * cell, -depth + row * depth, cell + 0.5, depth);
    }
  }
  ctx.restore();
}

